import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  MessageCircle,
  ShoppingCart,
  Package,
  Share2,
  Users,
  Gift,
  Repeat,
  ArrowRight,
  Flame,
  HeartHandshake,
  TrendingUp,
  Zap,
  Phone,
  Clock,
  Calendar,
  Award,
  Wallet,
  Link as LinkIcon,
  ChevronDown,
  MapPin,
  Shield,
  BarChart3,
  Target,
  Store,
  QrCode,
  Star,
  AlertTriangle,
  CreditCard,
  UtensilsCrossed,
  BadgeCheck,
} from 'lucide-react';

/* ───────────────────────────────────────────
   MoneyPrinting – HKSpicyValley Flywheel
   Brand-aligned dark theme with hero image
   ─────────────────────────────────────────── */

function SectionTitle({ children, subtitle }: { children: React.ReactNode; subtitle?: string }) {
  return (
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-3">
        {children}
      </h2>
      {subtitle && (
        <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">{subtitle}</p>
      )}
    </div>
  );
}

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

/* ── Hero ── */
function Hero() {
  return (
    <section className="relative text-white overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/media/media/1786030012_hkgspicyvalley_bg.jpeg')" }}
      />
      <div className="absolute inset-0 bg-x1493-dark/75" />

      <div className="absolute inset-0 opacity-10">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: Math.random() * 8 + 2 + 'px',
              height: Math.random() * 8 + 2 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 max-w-5xl mx-auto px-4 text-center"
      >
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8">
          <Flame className="w-5 h-5 text-x1493-secondary" />
          <span className="text-white/90 text-sm font-medium">合作夥伴專用 · 增長手冊</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight leading-tight">
          香港辣谷
          <br />
          <span className="text-x1493-secondary">印鈔機模式</span>
        </h1>

        <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          以餐廳優惠為流量入口，以港式辣味產品為核心商品，
          <br className="hidden md:block" />
          以推薦裂變為增長引擎，以數據資產為長期護城河。
        </p>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-white/50"
        >
          <ChevronDown className="w-8 h-8 mx-auto" />
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ── Strategy Positioning ── */
function StrategyPositioning() {
  const pillars = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: '流量入口',
      desc: '餐廳優惠與食肆合作',
      detail: '在香港餐飲業寒冬中，以「免費優惠」作為低成本獲客抓手，將線下客流轉化為線上私域會員。',
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: '核心商品',
      desc: '港式辣粉與辣油',
      detail: '優惠是鉤，產品是鉤上的餌。所有流量最終導向產品銷售，確保流量具備商業價值。',
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: '增長引擎',
      desc: '辣谷之友推薦裂變',
      detail: '顧客完成購買後自動轉為推薦夥伴，透過專屬連結與現金回贈形成自運轉的增長飛輪。',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: '長期護城河',
      desc: '數據與社群資產',
      detail: '掌握顧客行為、餐廳表現與推薦關係數據，並透過社群沉澱形成難以被複製的競爭壁壘。',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="我們的目標不是成為物流平台，而是成為辣味餐飲與消費者之間的流量與數據樞紐">
            戰略定位
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <FadeIn key={p.title} delay={i * 0.08}>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-x1493-primary to-orange-500 flex items-center justify-center text-white shadow-lg mb-4">
                  {p.icon}
                </div>
                <div className="text-xs font-bold text-x1493-secondary uppercase tracking-wider mb-1">{p.title}</div>
                <h3 className="text-lg font-bold text-white mb-2">{p.desc}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{p.detail}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.2}>
          <div className="mt-10 bg-gradient-to-r from-x1493-primary/15 to-orange-600/15 border border-x1493-primary/30 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-x1493-secondary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">平台思維，但不做重資產平台</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Zé Delivery 的成功，在於百威英博以配送網絡為基礎，將「冰鎮啤酒」變成「冰鎮飲料即時配送」的入口。
                  香港辣谷的資源禀赋不同，我們不應自建重資產物流，而應專注成為「港式辣味生活方式」的入口：
                  透過餐廳優惠獲取流量，透過產品銷售實現變現，透過推薦機制放大增長，透過數據資產構建護城河。
                </p>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Flywheel Diagram ── */
function FlywheelDiagram() {
  const steps = [
    {
      icon: <Phone className="w-8 h-8" />,
      title: '顧客接觸',
      desc: 'WhatsApp / 社群 / 優惠券',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: '首次下單',
      desc: '瀏覽 → 購物車 → 付款',
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: '訂單完成',
      desc: '發貨 → 自動旅程啟動',
      color: 'from-violet-500 to-violet-600',
    },
    {
      icon: <HeartHandshake className="w-8 h-8" />,
      title: '成為辣谷之友',
      desc: '顧客轉化為推薦夥伴',
      color: 'from-red-500 to-red-600',
      highlight: true,
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: '分享連結',
      desc: '專屬 ?r=XXXXXX 連結',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: '朋友落單',
      desc: '新顧客經推薦連結進入',
      color: 'from-pink-500 to-pink-600',
    },
    {
      icon: <Wallet className="w-8 h-8" />,
      title: '持續回贈',
      desc: '10% 現金回贈自動入帳',
      color: 'from-yellow-500 to-amber-600',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="每個環節緊密相扣，形成自動運轉的增長飛輪">
            印鈔機飛輪
          </SectionTitle>
        </FadeIn>

        {/* Desktop: circular layout */}
        <div className="hidden md:block relative">
          <div className="flex justify-center items-center mb-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
              className="relative w-[560px] h-[560px]"
            >
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/10" />

              {steps.map((step, i) => {
                const angle = (i / steps.length) * 360 - 90;
                const rad = (angle * Math.PI) / 180;
                const x = Math.cos(rad) * 230;
                const y = Math.sin(rad) * 230;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.12, type: 'spring' }}
                    className="absolute left-1/2 top-1/2"
                    style={{ x: x - 64, y: y - 64 }}
                  >
                    <div
                      className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${step.color} text-white flex flex-col items-center justify-center text-center p-3 shadow-lg ${
                        step.highlight ? 'ring-4 ring-x1493-secondary ring-offset-4 ring-offset-x1493-dark' : ''
                      }`}
                    >
                      <div className="mb-1">{step.icon}</div>
                      <div className="text-xs font-bold leading-tight">{step.title}</div>
                    </div>
                  </motion.div>
                );
              })}

              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full bg-gradient-to-br from-x1493-primary to-orange-600 text-white flex flex-col items-center justify-center shadow-xl z-10">
                <Flame className="w-8 h-8 mb-1" />
                <div className="text-sm font-black">辣谷之友</div>
                <div className="text-[10px] opacity-80">裂變核心</div>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-7 gap-3">
            {steps.map((step, i) => (
              <FadeIn key={step.title} delay={i * 0.08}>
                <div className="text-center">
                  <div className={`inline-flex p-2 rounded-xl bg-gradient-to-br ${step.color} text-white mb-2`}>
                    {step.icon}
                  </div>
                  <div className="text-sm font-bold text-white mb-1">{step.title}</div>
                  <div className="text-xs text-gray-400 leading-snug">{step.desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden space-y-6">
          {steps.map((step, i) => (
            <FadeIn key={step.title} delay={i * 0.1}>
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} text-white flex items-center justify-center shadow-lg`}>
                  {step.icon}
                </div>
                <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="font-bold text-white mb-1">{step.title}</div>
                  <div className="text-sm text-gray-400">{step.desc}</div>
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className="ml-7 w-0.5 h-6 bg-white/10" />
              )}
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Customer Journey Timeline ── */
function JourneyTimeline() {
  const [openTemplate, setOpenTemplate] = useState<string | null>(null);

  const milestones = [
    {
      time: 'T+15 分鐘',
      icon: <Clock className="w-5 h-5" />,
      title: '感謝與保存建議',
      desc: '透過貼心提醒建立信任與好感，為後續互動鋪墊。',
      color: 'bg-blue-500',
      templateKey: 'whatsapp_new_customer_thankyou',
      templateLabel: '查看感謝訊息',
      templateContent: `Hi {name}，多謝你選擇 *香港辣谷* ！🌶️\n\n你嘅支持對我哋好重要。辣粉最好存放喺 *陰涼乾爽處* ，開封後建議放 *雪櫃* ，風味可以保持得更耐。\n\n食完之後歡迎分享你嘅食法或者打卡，我哋很樂意見到辣粉出現喺你嘅餐桌上 😊`,
    },
    {
      time: 'T+7 天',
      icon: <Calendar className="w-5 h-5" />,
      title: '食譜靈感',
      desc: '提供創意用法，維持顧客互動熱度並降低流失。',
      color: 'bg-emerald-500',
      templateKey: 'whatsapp_new_customer_recipe',
      templateLabel: '查看食譜靈感訊息',
      templateContent: `Hi {name}，辣粉食得點？😋\n\n想換換口味？試下用辣谷辣粉 *炒蛋* 、 *撈麵* 或者 *醃雞翼* ，簡單又好味。\n\n如果你試到新配搭，歡迎分享畀我哋，我哋會推介畀更多辣友一齊試 🔥`,
    },
    {
      time: 'T+21 天',
      icon: <Zap className="w-5 h-5" />,
      title: '補貨提醒',
      desc: '溫和提醒補貨，附上直接落單連結，縮短決策路徑。',
      color: 'bg-violet-500',
      templateKey: 'whatsapp_new_customer_restock',
      templateLabel: '查看補貨提醒訊息',
      templateContent: `Hi {name}，屋企辣粉庫存還好嗎？🔥\n\n如果快食完，可以提早鋪排補貨，我哋幫你預留 *最新鮮嘅貨* 。\n\n直接落單：\n{orderLink}\n\n回覆我哋，可以幫你揀最啱嘅組合。`,
    },
    {
      time: 'T+28 天',
      icon: <Gift className="w-5 h-5" />,
      title: '回購與辣谷之友邀請',
      desc: '將高滿意度顧客轉化為推薦夥伴，啟動裂變機制。',
      color: 'bg-red-500',
      highlight: true,
      templateKey: 'whatsapp_new_customer_repurchase',
      templateLabel: '查看回購邀約訊息',
      templateContent: `Hi {name}，屋企辣粉庫存還好嗎？😊\n\n如果你都想補貨，呢度可以直接落單：\n{orderLink}\n\n另外，如果你鍾意 *香港辣谷* 嘅味道，其實可以一邊食辣一邊把 *正宗港式辣粉* 分享畀朋友。你 *唔使投資* 、 *唔使處理物流* ，只要分享你嘅 *專屬連結* ，朋友落單你就可以輕鬆賺到回贈。\n\n分享連結：\n{referralLink}\n\n隨時睇 *朋友支持同回贈* ：\n{dashboardLink}\n\n有咩疑問，歡迎直接回覆我哋 🔥`,
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-3xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="訂單完成後自動觸發，以零人工干預持續培育顧客關係">
            顧客自動旅程
          </SectionTitle>
        </FadeIn>

        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 via-emerald-400 via-violet-400 to-red-500" />

          <div className="space-y-8">
            {milestones.map((m, i) => (
              <FadeIn key={m.time} delay={i * 0.15}>
                <div className="relative flex items-start gap-6">
                  <div className="relative z-10 flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full ${m.color} text-white flex items-center justify-center shadow-lg ${m.highlight ? 'ring-4 ring-x1493-secondary' : ''}`}>
                      {m.icon}
                    </div>
                  </div>

                  <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-5">
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-2 ${m.color}`}>
                      {m.time}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">{m.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-3">{m.desc}</p>
                    {m.templateContent && (
                      <div>
                        <button
                          onClick={() => setOpenTemplate(openTemplate === m.templateKey ? null : m.templateKey!)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-x1493-secondary hover:text-x1493-primary transition mb-2"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          {openTemplate === m.templateKey ? '收起訊息' : m.templateLabel}
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openTemplate === m.templateKey ? 'rotate-180' : ''}`} />
                        </button>
                        {openTemplate === m.templateKey && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="bg-black/30 border border-white/10 rounded-lg p-3 overflow-hidden"
                          >
                            <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap leading-relaxed">
                              {m.templateContent}
                            </pre>
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Offer Tiering & Quality Control ── */
function OfferTiering() {
  const tiers = [
    {
      icon: <Gift className="w-6 h-6" />,
      title: '公開優惠',
      desc: '無需門檻，任何人可領取',
      detail: '用於最大範圍引流，例如「免費獲贈日式煎餃一碟」。領取上限與每日上限必須設定，避免被濫用。',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: '辣谷之友優惠',
      desc: '需註冊或加入社群後領取',
      detail: '用於沉澱私域流量。顧客必須完成電話驗證或加入 Facebook 群組，才能解鎖更優惠的餐廳禮遇。',
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: '消費後優惠',
      desc: '完成首單後解鎖',
      detail: '用於確保流量轉化為銷售。顧客購買任何產品後，可獲得更高價值的餐廳優惠或產品折扣。',
    },
  ];

  const controls = [
    {
      icon: <BadgeCheck className="w-5 h-5" />,
      title: '防刷機制',
      desc: '每個優惠設置總名額、每日上限、每電話一次，並以 Deep Link 電話驗證確保真實性。',
    },
    {
      icon: <Star className="w-5 h-5" />,
      title: '兌換後評分',
      desc: '兌換成功後 24 小時自動發送 WhatsApp 評分請求；連續低分餐廳自動暫停優惠並通知管理員。',
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: '餐廳審批標準',
      desc: '圖片品質、兌換流程、衛生條件需達標；管理員保留最終審批權，確保「辣谷嚴選」品質。',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="優惠是引流工具，不是免費午餐；必須設計分層與防護機制">
            優惠分層與品質控制
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {tiers.map((t, i) => (
            <FadeIn key={t.title} delay={i * 0.08}>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-x1493-primary to-orange-500 flex items-center justify-center text-white shadow-lg mb-4">
                  {t.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{t.title}</h3>
                <div className="text-sm font-medium text-x1493-secondary mb-2">{t.desc}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{t.detail}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {controls.map((c, i) => (
            <FadeIn key={c.title} delay={i * 0.08}>
              <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-x1493-secondary flex-shrink-0">
                  {c.icon}
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">{c.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Referral Mechanics ── */
function ReferralMechanics() {
  const benefits = [
    {
      icon: <LinkIcon className="w-6 h-6" />,
      title: '專屬分享連結',
      desc: '每位辣谷之友獲得獨一無二的短連結，一鍵分享至 WhatsApp、IG 或 Facebook。',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: '10% 現金回贈',
      desc: '朋友每次落單，自動計算 10% 回贈（100 分 = HK$1），餘額達 HK$20 即可抵扣。',
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: '里程碑獎勵',
      desc: '推薦 5/10/25 單或 50/100/500 瀏覽達標時，自動祝賀並附贈小禮物。',
    },
    {
      icon: <Repeat className="w-6 h-6" />,
      title: '永久綁定',
      desc: '朋友首次落單後永久綁定，之後每單都自動計算回贈，形成長期被動收入。',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark text-white px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-3">裂變收益機制</h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              顧客一旦成為「辣谷之友」，整個推薦機器自動運轉
            </p>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {benefits.map((b, i) => (
            <FadeIn key={b.title} delay={i * 0.1}>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-x1493-primary to-orange-500 flex items-center justify-center shadow-lg">
                    {b.icon}
                  </div>
                  <h3 className="text-lg font-bold">{b.title}</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn>
          <div className="bg-gradient-to-r from-x1493-primary/20 to-orange-600/20 border border-x1493-primary/30 rounded-3xl p-8 md:p-12 text-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-6">
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-5 py-3">
                <Users className="w-5 h-5 text-x1493-secondary" />
                <span className="font-bold">顧客 A</span>
              </div>
              <ArrowRight className="w-6 h-6 text-x1493-primary hidden md:block" />
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-5 py-3">
                <Share2 className="w-5 h-5 text-x1493-secondary" />
                <span className="font-bold">分享連結</span>
              </div>
              <ArrowRight className="w-6 h-6 text-x1493-primary hidden md:block" />
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-5 py-3">
                <Users className="w-5 h-5 text-x1493-secondary" />
                <span className="font-bold">顧客 B</span>
              </div>
              <ArrowRight className="w-6 h-6 text-x1493-primary hidden md:block" />
              <div className="flex items-center gap-3 bg-gradient-to-r from-x1493-secondary to-amber-500 rounded-full px-5 py-3 shadow-lg">
                <Wallet className="w-5 h-5 text-white" />
                <span className="font-bold text-white">A 賺回贈</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              顧客 B 完成旅程後，同樣可被邀請成為辣谷之友 → 繼續裂變
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Restaurant Partnership ── */
function RestaurantPartnership() {
  const models = [
    {
      icon: <Store className="w-6 h-6" />,
      title: '免費試行期',
      desc: '新餐廳首兩週免費上架',
      detail: '降低合作門檻，快速累積案例與數據。試行期滿後，根據領取與兌換數據商談收費模式。',
    },
    {
      icon: <UtensilsCrossed className="w-6 h-6" />,
      title: '聯名菜式',
      desc: '«X1493» 聯名辣味限定',
      detail: '餐廳採用香港辣谷產品入菜，我們提供流量與品牌背書；顧客掃碼領取優惠，同時推廣產品批發。',
    },
    {
      icon: <QrCode className="w-6 h-6" />,
      title: '付費置頂與流量包',
      desc: '商家後台自助投放',
      detail: '餐廳可付費購買置頂位置、加圖、Pop-up 曝光或領取流量包；管理員保留審批權，確保品質。',
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: 'B2B 批發',
      desc: '餐廳直接掃碼下單批發',
      detail: '透過現有產品系統，為餐廳提供辣粉批發價；將流量合作升級為供應鏈合作。',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="餐廳優惠不是單次活動，而是可持續的 B2B2C 合作模式">
            餐廳合作模式
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-6">
          {models.map((m, i) => (
            <FadeIn key={m.title} delay={i * 0.08}>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-x1493-primary to-orange-500 flex items-center justify-center text-white shadow-lg">
                    {m.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{m.title}</h3>
                    <div className="text-sm text-x1493-secondary">{m.desc}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed">{m.detail}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.2}>
          <div className="mt-10 bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-x1493-secondary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">WhatsApp 一鍵領取，自動註冊為辣谷之友</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-3">
                  顧客發送關鍵字即可自動領取優惠券與兌換碼，系統同時將其註冊為辣谷之友，把線下客流轉化為線上裂變種子。
                </p>
                <div className="bg-black/30 border border-white/10 rounded-lg p-3 text-sm font-mono text-gray-300">
                  香港辣谷優惠 OFFER:sake-yakitori-free
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Monetization Path ── */
function MonetizationPath() {
  const paths = [
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      stage: '短期（0–3 個月）',
      title: '產品銷售',
      desc: '以優惠引流，透過辣粉、辣油及組合裝實現現金流。',
    },
    {
      icon: <Store className="w-6 h-6" />,
      stage: '中期（3–6 個月）',
      title: '餐廳收費',
      desc: '付費置頂、流量包、數據報告，將流量資產轉化為 B2B 收入。',
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      stage: '長期（6–12 個月）',
      title: '訂閱與會員',
      desc: '辣粉月盒、年度「辣谷卡」（免運費、專屬優惠、雙倍佣金），建立經常性收入。',
    },
    {
      icon: <Package className="w-6 h-6" />,
      stage: 'B2B 供應鏈',
      title: '辣粉批發',
      desc: '餐廳、酒樓及食材批發商直接掃碼下單，將流量合作升級為供應鏈合作。',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="從單次交易走向多元收入，降低對單一變現模式的依賴">
            變現路徑
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {paths.map((p, i) => (
            <FadeIn key={p.title} delay={i * 0.08}>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 h-full">
                <div className="text-xs font-bold text-x1493-secondary uppercase tracking-wider mb-3">{p.stage}</div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-x1493-primary to-orange-500 flex items-center justify-center text-white shadow-lg mb-4">
                  {p.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{p.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{p.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Metrics & KPI ── */
function MetricsSection() {
  const funnel = [
    { label: 'Impression', desc: '優惠被看到', target: '曝光量' },
    { label: 'Claim', desc: '領取優惠', target: '≥15% 領取率' },
    { label: 'Redeem', desc: '實際兌換', target: '≥40% 兌換率' },
    { label: 'Purchase', desc: '之後購買產品', target: '≥10% 轉化率' },
    { label: 'Repeat', desc: '30 日內回購', target: '≥20% 復購率' },
  ];

  const kpis = [
    'Claim → Purchase 轉化率（目標 ≥10%）',
    '30 日復購率（目標 ≥20%）',
    '推薦裂變系數（每位推薦人帶來多少新客）',
    '每個優惠的 ROI（餐廳是否願意續費）',
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="數據不是用來看，而是用來做決策">
            數據與關鍵指標
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-5 gap-4 mb-12">
          {funnel.map((f, i) => (
            <FadeIn key={f.label} delay={i * 0.06}>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <div className="text-sm font-black text-white mb-1">{f.label}</div>
                <div className="text-xs text-gray-400 mb-2">{f.desc}</div>
                <div className="text-xs font-medium text-x1493-secondary">{f.target}</div>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-6 h-6 text-x1493-secondary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-3">每週餐廳表現報告</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-4">
                  系統每週自動向合作餐廳發送表現報告，包含領取數、兌換率、顧客評分與轉化數據，提升商家黏性與議價能力。
                </p>
                <ul className="space-y-2">
                  {kpis.map((k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-gray-300">
                      <span className="text-x1493-secondary mt-0.5">•</span>
                      {k}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── Risk Control ── */
function RiskControl() {
  const risks = [
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: 'WhatsApp 單點依賴',
      desc: '關鍵通知（訂單確認、付款提醒）需設置第二發送通道（如 SMS 或 Email），並建立 failed message 人工接手清單。',
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: '餐廳品質風險',
      desc: '建立兌換後評分機制與餐廳審批標準；連續低分餐廳自動暫停，避免壞體驗傷害品牌。',
    },
    {
      icon: <Phone className="w-5 h-5" />,
      title: '客訴與 SLA',
      desc: '設定 24 小時內回覆準則，並於後台標記高風險對話，確保顧客體驗不被優惠活動拖累。',
    },
    {
      icon: <Gift className="w-5 h-5" />,
      title: '優惠濫用風險',
      desc: '所有優惠需設定總名額、每日上限、每電話一次，並以 Deep Link 電話驗證確保真實性。',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="增長越快，風險控制越不能鬆懈">
            風險控制與基礎設施
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-6">
          {risks.map((r, i) => (
            <FadeIn key={r.title} delay={i * 0.08}>
              <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-x1493-secondary flex-shrink-0">
                  {r.icon}
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">{r.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{r.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Action Plan ── */
function ActionPlan() {
  const plans = [
    {
      period: '30 天內',
      color: 'from-blue-500 to-blue-600',
      items: [
        '所有優惠設置領取上限與每電話一次限制',
        '上線兌換後 24 小時評分 Workflow',
        '建立優惠漏斗 Dashboard（Impression → Claim → Redeem → Purchase）',
        '準備 WhatsApp 備援通道與關鍵通知 fallback',
      ],
    },
    {
      period: '60 天內',
      color: 'from-emerald-500 to-emerald-600',
      items: [
        '推出「辣谷之友」分層會員制度',
        '與 2–3 間餐廳試行聯名菜式',
        '商家後台加入付費置頂與流量包選項',
        '啟動每週辣評榜，透過 WhatsApp broadcast 推送',
      ],
    },
    {
      period: '90 天內',
      color: 'from-violet-500 to-violet-600',
      items: [
        '推出「辣粉月盒」訂閱制',
        '推出年度「辣谷卡」（免運費、專屬優惠、雙倍佣金）',
        '建立 B2B 批發落單頁面',
        '與食材批發商或酒樓集團洽談合作',
      ],
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-x1493-dark px-4 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <SectionTitle subtitle="策略必須拆解為可執行的行動，才能將天上的結果落地變現">
            30 / 60 / 90 天行動清單
          </SectionTitle>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <FadeIn key={p.period} delay={i * 0.1}>
              <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden h-full">
                <div className={`bg-gradient-to-r ${p.color} px-6 py-4`}>
                  <div className="text-lg font-black text-white">{p.period}</div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-gray-300">
                        <BadgeCheck className="w-4 h-4 text-x1493-secondary mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA ── */
function CTA() {
  return (
    <section className="py-20 md:py-28 bg-x1493-primary text-white px-4 text-center">
      <FadeIn>
        <Flame className="w-16 h-16 mx-auto mb-6 text-x1493-secondary" />
        <h2 className="text-3xl md:text-5xl font-black mb-4">
          準備好啟動你的印鈔機了嗎？
        </h2>
        <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-8">
          每個顧客都是潛在的推廣合夥人，每間餐廳都是潛在的流量入口。
          讓我們一起把香港辣谷打造成香港辣味社群最識揀嘅入口。
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://www.facebook.com/groups/2637062093216141"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-white text-x1493-primary px-8 py-4 rounded-xl font-bold text-lg hover:bg-x1493-secondary hover:text-x1493-dark transition shadow-lg"
          >
            <Users className="w-5 h-5" />
            加入辣谷之友 Facebook Group
          </a>
          <a
            href="https://hkspicyvalley.com"
            className="inline-flex items-center justify-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/25 transition"
          >
            <ArrowRight className="w-5 h-5" />
            瀏覽香港辣谷
          </a>
        </div>
      </FadeIn>
    </section>
  );
}

/* ── Footer ── */
function Footer() {
  return (
    <footer className="bg-black text-gray-500 py-8 text-center text-sm border-t border-white/5">
      <p>© 2026 香港辣谷 HKSpicyValley · 屬於辣腳嘅共同品牌</p>
    </footer>
  );
}

/* ── Page ── */
export default function MoneyPrinting() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-x1493-dark">
      <Hero />
      <StrategyPositioning />
      <FlywheelDiagram />
      <JourneyTimeline />
      <OfferTiering />
      <ReferralMechanics />
      <RestaurantPartnership />
      <MonetizationPath />
      <MetricsSection />
      <RiskControl />
      <ActionPlan />
      <CTA />
      <Footer />
    </div>
  );
}
